import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiFetch, isApiError } from '../api/client'
import { AuthCard } from '../components/AuthCard'
import { safeNext, withNext } from '../context/pendingInvite'

type SignupState = 'idle' | 'loading' | 'error' | 'sent'

const MIN_PASSWORD = 8

function retryText(seconds: number | undefined): string {
  const mins = Math.max(1, Math.ceil((seconds ?? 900) / 60))
  return mins === 1 ? 'about a minute' : `about ${mins} minutes`
}

/** One plain sentence per POST /auth/signup failure (400/409/422/429/503). */
function signupErrorMessage(err: unknown): string {
  if (!isApiError(err)) return 'Something went wrong, please try again.'
  if (err.status === 409) return 'An account with this email already exists. Sign in, or reset your password.'
  if (err.status === 422 && err.code === 'disposable_email_domain') {
    return 'Disposable email addresses can’t be used. Sign up with a permanent address.'
  }
  if (err.status === 422) return `Password does not meet requirements (at least ${MIN_PASSWORD} characters).`
  if (err.status === 400) return 'Enter a valid email and password.'
  if (err.status === 429) {
    return `Too many sign-up attempts from this network. Try again in ${retryText(err.retry_after_seconds)}.`
  }
  return 'Sign-up is temporarily unavailable. Please try again in a moment.'
}

export function SignupView() {
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [state, setState] = useState<SignupState>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [sentTo, setSentTo] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (password.length < MIN_PASSWORD) {
      setState('error')
      setErrorMsg(`Password must be at least ${MIN_PASSWORD} characters.`)
      return
    }
    setState('loading')
    setErrorMsg('')
    const body: Record<string, string> = { email: email.trim(), password }
    if (firstName.trim()) body.first_name = firstName.trim()
    if (lastName.trim()) body.last_name = lastName.trim()
    try {
      await apiFetch('/auth/signup', { method: 'POST', body: JSON.stringify(body) })
      setSentTo(body.email)
      setPassword('')
      setState('sent')
    } catch (err) {
      setState('error')
      setErrorMsg(signupErrorMessage(err))
    }
  }

  const signIn = <Link to={withNext('/login', next)}>Sign in</Link>

  if (state === 'sent') {
    return (
      <AuthCard title="Check your email" footer={<span>Confirmed? {signIn}</span>}>
        <p role="status" className="form-notice">
          We sent a confirmation link to <strong>{sentTo}</strong>. Open it to activate your
          account, then sign in.
        </p>
      </AuthCard>
    )
  }

  const busy = state === 'loading'
  return (
    <AuthCard
      title="Create your account"
      subtitle="Start building with Symone."
      footer={<span>Already have an account? {signIn}</span>}
    >
      <form onSubmit={handleSubmit} aria-label="Sign up form" noValidate>
        <div className="field-row">
          <div className="field">
            <label htmlFor="signup-first">First name <span className="muted">(optional)</span></label>
            <input id="signup-first" autoComplete="given-name" value={firstName}
              onChange={(e) => setFirstName(e.target.value)} disabled={busy} />
          </div>
          <div className="field">
            <label htmlFor="signup-last">Last name <span className="muted">(optional)</span></label>
            <input id="signup-last" autoComplete="family-name" value={lastName}
              onChange={(e) => setLastName(e.target.value)} disabled={busy} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="signup-email">Email</label>
          <input id="signup-email" type="email" autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
        </div>
        <div className="field">
          <label htmlFor="signup-password">Password</label>
          <input id="signup-password" type="password" autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} disabled={busy} required
            minLength={MIN_PASSWORD} aria-describedby="signup-password-hint" />
          <span id="signup-password-hint" className="field-hint">At least {MIN_PASSWORD} characters.</span>
        </div>
        {state === 'error' && <p role="alert" className="form-error">{errorMsg}</p>}
        <button type="submit" className="btn btn--primary btn--block"
          disabled={busy || !email.trim() || !password} aria-busy={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthCard>
  )
}
